import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Alert,
  Paper,
  Grid,
  Divider,
  FormHelperText,
  Chip,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

const AutofocusStep = ({ observatoryDefaults = null }) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext();

  // Toggle is backed by react-hook-form state so it survives wizard step
  // navigation (steps unmount) and propagates to the review step + save payload.
  const useDefaults = watch('useDefaults') ?? true;

  // Watch form values
  const afEnabled = watch('afEnabled');
  const initialAutofocus = watch('initialAutofocus');
  const timeBasedEnabled = watch('afTimeBased');
  const timeBasedInterval = watch('afTimeInterval');
  const tempBasedEnabled = watch('afTempBased');
  const tempThreshold = watch('afTempThreshold');
  const hfrBasedEnabled = watch('afHfrBased');
  const hfrPercentage = watch('afHfrPercentage');
  const filterChangeEnabled = watch('afFilterChange');
  const afterExposuresEnabled = watch('afAfterExposures');
  const afterExposuresCount = watch('afAfterExposuresCount');

  // Derive defaults from observatory settings
  const obsTriggers = observatoryDefaults?.autofocus_settings?.triggers || {};

  // Load observatory defaults on mount
  useEffect(() => {
    if (observatoryDefaults?.autofocus_settings && useDefaults) {
      const settings = observatoryDefaults.autofocus_settings;
      setValue('afEnabled', settings.enabled ?? true);
      setValue('initialAutofocus', settings.initial_autofocus ?? true);
      setValue('afTimeBased', obsTriggers.time_based?.enabled ?? true);
      setValue('afTimeInterval', obsTriggers.time_based?.interval_minutes ?? 30);
      setValue('afTempBased', obsTriggers.temperature_based?.enabled ?? false);
      setValue('afTempThreshold', obsTriggers.temperature_based?.threshold_celsius ?? 2.0);
      setValue('afHfrBased', obsTriggers.hfr_based?.enabled ?? false);
      setValue('afHfrPercentage', obsTriggers.hfr_based?.increase_percentage ?? 5);
      setValue('afFilterChange', obsTriggers.filter_change?.enabled ?? false);
      setValue('afAfterExposures', obsTriggers.after_exposures?.enabled ?? false);
      setValue('afAfterExposuresCount', obsTriggers.after_exposures?.exposure_count ?? 10);
    }
  }, [observatoryDefaults, setValue, useDefaults, obsTriggers]);

  const handleUseDefaultsToggle = (event) => {
    setValue('useDefaults', event.target.checked);

    if (event.target.checked && observatoryDefaults?.autofocus_settings) {
      const settings = observatoryDefaults.autofocus_settings;
      setValue('afEnabled', settings.enabled ?? true);
      setValue('initialAutofocus', settings.initial_autofocus ?? true);
      setValue('afTimeBased', obsTriggers.time_based?.enabled ?? true);
      setValue('afTimeInterval', obsTriggers.time_based?.interval_minutes ?? 30);
      setValue('afTempBased', obsTriggers.temperature_based?.enabled ?? false);
      setValue('afTempThreshold', obsTriggers.temperature_based?.threshold_celsius ?? 2.0);
      setValue('afHfrBased', obsTriggers.hfr_based?.enabled ?? false);
      setValue('afHfrPercentage', obsTriggers.hfr_based?.increase_percentage ?? 5);
      setValue('afFilterChange', obsTriggers.filter_change?.enabled ?? false);
      setValue('afAfterExposures', obsTriggers.after_exposures?.enabled ?? false);
      setValue('afAfterExposuresCount', obsTriggers.after_exposures?.exposure_count ?? 10);
    }
  };

  // Build summary description of observatory defaults
  const defaultSummary = () => {
    if (!observatoryDefaults?.autofocus_settings) return null;
    const s = observatoryDefaults.autofocus_settings;
    if (s.enabled === false) return 'External AF management (plugin AF disabled)';
    const parts = [];
    if (s.initial_autofocus) parts.push('initial AF at start');
    if (obsTriggers.time_based?.enabled) parts.push(`AF every ${obsTriggers.time_based.interval_minutes || 30} min`);
    if (obsTriggers.temperature_based?.enabled) parts.push(`temp >${obsTriggers.temperature_based.threshold_celsius || 2}°C`);
    if (obsTriggers.hfr_based?.enabled) parts.push('HFR-based');
    if (obsTriggers.filter_change?.enabled) parts.push('on filter change');
    if (obsTriggers.after_exposures?.enabled) parts.push(`every ${obsTriggers.after_exposures.exposure_count || 10} exposures`);
    return parts.length > 0 ? parts.join(', ') : 'No AF triggers configured';
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Autofocus Settings
      </Typography>

      {observatoryDefaults && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon fontSize="small" />
            <Typography variant="body2">
              Observatory default: {defaultSummary()}
            </Typography>
          </Box>
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={useDefaults}
              onChange={handleUseDefaultsToggle}
              color="primary"
            />
          }
          label="Use observatory defaults"
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          {/* Master AF toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={afEnabled ?? true}
                  onChange={(e) => setValue('afEnabled', e.target.checked)}
                  disabled={useDefaults}
                  color="primary"
                />
              }
              label="Plugin-managed autofocus"
            />
            <FormHelperText>
              Disable to manage autofocus externally via NINA Advanced Sequencer
            </FormHelperText>
          </Grid>

          {/* Initial AF */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={initialAutofocus ?? true}
                  onChange={(e) => setValue('initialAutofocus', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="Perform initial autofocus"
            />
            <FormHelperText>
              Run autofocus at the start of this observation
            </FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Trigger Configuration
            </Typography>
          </Grid>

          {/* Time-based */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={timeBasedEnabled ?? true}
                  onChange={(e) => setValue('afTimeBased', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="Time-based autofocus"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              {...register('afTimeInterval', {
                min: { value: 5, message: 'Minimum 5 minutes' },
                max: { value: 180, message: 'Maximum 180 minutes' }
              })}
              label="Interval (minutes)"
              type="number"
              fullWidth
              disabled={useDefaults || !afEnabled || !timeBasedEnabled}
              error={!!errors.afTimeInterval}
              helperText={errors.afTimeInterval?.message || 'Run AF every N minutes'}
              InputProps={{ inputProps: { min: 5, max: 180, step: 5 } }}
            />
          </Grid>

          {/* Temperature-based */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={tempBasedEnabled ?? false}
                  onChange={(e) => setValue('afTempBased', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="Temperature-based autofocus"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              {...register('afTempThreshold', {
                min: { value: 0.5, message: 'Minimum 0.5°C' },
                max: { value: 10, message: 'Maximum 10°C' }
              })}
              label="Temperature Threshold (°C)"
              type="number"
              fullWidth
              disabled={useDefaults || !afEnabled || !tempBasedEnabled}
              error={!!errors.afTempThreshold}
              helperText={errors.afTempThreshold?.message || 'Temperature change to trigger AF'}
              InputProps={{ inputProps: { min: 0.5, max: 10, step: 0.5 } }}
            />
          </Grid>

          {/* HFR-based */}
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={hfrBasedEnabled ?? false}
                  onChange={(e) => setValue('afHfrBased', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="HFR-based autofocus"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              {...register('afHfrPercentage', {
                min: { value: 1, message: 'Minimum 1%' },
                max: { value: 100, message: 'Maximum 100%' }
              })}
              label="HFR Increase (%)"
              type="number"
              fullWidth
              disabled={useDefaults || !afEnabled || !hfrBasedEnabled}
              error={!!errors.afHfrPercentage}
              helperText={errors.afHfrPercentage?.message || '% increase to trigger AF'}
              InputProps={{ inputProps: { min: 1, max: 100, step: 1 } }}
            />
          </Grid>
          <Grid item xs={12} md={4} />

          {/* Filter change */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterChangeEnabled ?? false}
                  onChange={(e) => setValue('afFilterChange', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="Autofocus on filter change"
            />
          </Grid>

          {/* After N exposures */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={afterExposuresEnabled ?? false}
                  onChange={(e) => setValue('afAfterExposures', e.target.checked)}
                  disabled={useDefaults || !afEnabled}
                  color="primary"
                />
              }
              label="Autofocus after N exposures"
            />
          </Grid>
          {afterExposuresEnabled && (
            <Grid item xs={12} md={6}>
              <TextField
                {...register('afAfterExposuresCount', {
                  min: { value: 1, message: 'Minimum 1' },
                  max: { value: 1000, message: 'Maximum 1000' }
                })}
                label="Exposure Count"
                type="number"
                fullWidth
                disabled={useDefaults || !afEnabled}
                error={!!errors.afAfterExposuresCount}
                helperText={errors.afAfterExposuresCount?.message || 'Run AF every N exposures'}
                InputProps={{ inputProps: { min: 1, max: 1000, step: 1 } }}
              />
            </Grid>
          )}
        </Grid>

        {!useDefaults && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Custom Settings Summary:</strong>
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!afEnabled && (
                <Chip label="AF disabled (external)" size="small" color="warning" variant="outlined" />
              )}
              {afEnabled && initialAutofocus && (
                <Chip label="Initial AF" size="small" color="primary" variant="outlined" />
              )}
              {afEnabled && timeBasedEnabled && (
                <Chip label={`AF every ${timeBasedInterval} min`} size="small" color="primary" variant="outlined" />
              )}
              {afEnabled && tempBasedEnabled && (
                <Chip label={`Temp >${tempThreshold}°C`} size="small" color="secondary" variant="outlined" />
              )}
              {afEnabled && hfrBasedEnabled && (
                <Chip label={`HFR +${hfrPercentage}%`} size="small" color="secondary" variant="outlined" />
              )}
              {afEnabled && filterChangeEnabled && (
                <Chip label="Filter change" size="small" color="info" variant="outlined" />
              )}
              {afEnabled && afterExposuresEnabled && (
                <Chip label={`Every ${afterExposuresCount} exp`} size="small" color="info" variant="outlined" />
              )}
              {afEnabled && !initialAutofocus && !timeBasedEnabled && !tempBasedEnabled && !hfrBasedEnabled && !filterChangeEnabled && !afterExposuresEnabled && (
                <Chip label="No AF triggers" size="small" color="warning" variant="outlined" />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> These settings override the observatory defaults for this observation only.
          The actual autofocus behavior may be modified by the telescope operator if needed.
        </Typography>
      </Alert>
    </Box>
  );
};

export default AutofocusStep;
